/**
 * Almikia Epic EW2 (TASK-079): pure view helpers of the two WhatsApp clock bars. The browser
 * only RENDERS the remaining time of a deadline the server computed (offset from the server
 * `now` returned with it); whether a window is open, and what a message costs, is decided by the
 * database, never here. These helpers only turn a remaining duration into a colour and a label.
 */
export const ONE_HOUR_MS = 3_600_000;
export const SIX_HOURS_MS = 6 * ONE_HOUR_MS;
const MINUTE_MS = 60_000;
const DAY_MS = 24 * ONE_HOUR_MS;

/** `closed` is the "Solo plantillas" / "Los mensajes ya tienen costo" state (nothing left). */
export type ClockTone = "green" | "yellow" | "red" | "closed";

/** Milliseconds to add to the browser clock to get the server clock (server `now` at read time). */
export const serverOffsetMs = (serverNowIso: string, clientNowMs: number) =>
  new Date(serverNowIso).getTime() - clientNowMs;

/** Remaining time of a server deadline, never negative; `null` deadline means no clock. */
export const remainingMs = (
  deadlineIso: string | null,
  clientNowMs: number,
  offsetMs: number,
): number | null =>
  deadlineIso == null
    ? null
    : Math.max(0, new Date(deadlineIso).getTime() - (clientNowMs + offsetMs));

/**
 * Colour of the 24 h "Escribir libre" bar: green ABOVE 6 h, yellow at or below 6 h, red BELOW 1 h,
 * closed at zero. Exactly 6 h is yellow and exactly 1 h is yellow (strict thresholds).
 */
export const freeTextTone = (remaining: number): ClockTone => {
  if (remaining <= 0) return "closed";
  if (remaining < ONE_HOUR_MS) return "red";
  if (remaining <= SIX_HOURS_MS) return "yellow";
  return "green";
};

/** The 7-day "Mensajes gratis" bar has one colour while open (it is a free-cost window). */
export const freeMessagesTone = (remaining: number): ClockTone =>
  remaining <= 0 ? "closed" : "green";

/** Compact countdown text: "6 d 4 h", "5 h 30 min", "12 min", "0 min". Rounded down. */
export const formatRemaining = (remaining: number): string => {
  const total = Math.max(0, remaining);
  const days = Math.floor(total / DAY_MS);
  const hours = Math.floor((total % DAY_MS) / ONE_HOUR_MS);
  const minutes = Math.floor((total % ONE_HOUR_MS) / MINUTE_MS);
  if (days > 0) return `${days} d ${hours} h`;
  if (hours > 0) return `${hours} h ${minutes} min`;
  return `${minutes} min`;
};

/** Fill of a bar from 0 to 1 over a nominal window length (display scaling only). */
export const barFraction = (remaining: number, windowMs: number): number =>
  Math.min(1, Math.max(0, remaining / windowMs));

export const FREE_TEXT_WINDOW_MS = DAY_MS;
export const FREE_MESSAGES_WINDOW_MS = 7 * DAY_MS;

export const TONE_COLORS: Record<ClockTone, string> = {
  green: "#3BB273",
  yellow: "#F1D432",
  red: "#D6452B",
  closed: "#6B6F8A",
};
